import { useState, useEffect } from "react";
import axios from "axios";
import { CreditCard, Loader2, ChevronLeft, Check } from "lucide-react";
import { toast } from "sonner";

import { API } from "@/apiConfig";

export default function BankCardPage({ onBack }) {
  const [bankName, setBankName] = useState("");
  const [accountName, setAccountName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [ifsc, setIfsc] = useState("");
  const [upiId, setUpiId] = useState("");
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    fetchCard();
  }, []);

  const fetchCard = async () => {
    try {
      const { data } = await axios.get(`${API}/user/bank-card`);
      if (data && data.bank_account_name) {
        setBankName(data.bank_name || "");
        setAccountName(data.bank_account_name || "");
        setAccountNumber(data.bank_account_number || "");
        setIfsc(data.bank_ifsc || "");
        setUpiId(data.upi_id || "");
        setSaved(true);
      }
    } catch { }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!accountName || !accountNumber) {
      toast.error("Account name and number are required");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/user/bank-card`, {
        bank_account_name: accountName,
        bank_account_number: accountNumber,
        bank_ifsc: ifsc,
        bank_name: bankName,
        upi_id: upiId,
      });
      toast.success("Bank details saved");
      setSaved(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="me-page" data-testid="bank-card-page">
      <h1 className="page-title heading-serif">Bank Card</h1>
      <button className="back-btn" onClick={onBack} data-testid="bankcard-back-btn">
        <ChevronLeft size={16} className="inline" /> Back
      </button>

      {saved && (
        <div className="bank-card-section mb-4" data-testid="saved-card-display">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-[rgba(212,175,55,0.15)] flex items-center justify-center">
              <CreditCard size={18} className="text-[#d4af37]" />
            </div>
            <div>
              <p className="text-white font-medium">{accountName}</p>
              <p className="text-xs text-slate-400">{bankName} - ****{accountNumber.slice(-4)}</p>
            </div>
            <Check size={18} className="text-green-400 ml-auto" />
          </div>
        </div>
      )}

      <form onSubmit={handleSave} className="bank-card-section space-y-3" data-testid="bank-card-form">
        <div>
          <label className="auth-label">Account Holder Name</label>
          <input type="text" className="auth-input" placeholder="Full name" value={accountName}
            onChange={(e) => setAccountName(e.target.value)} data-testid="bank-name-input" />
        </div>
        <div>
          <label className="auth-label">Bank Account Number</label>
          <input type="text" className="auth-input" placeholder="Account number" value={accountNumber}
            onChange={(e) => setAccountNumber(e.target.value)} data-testid="bank-account-input" />
        </div>
        <div>
          <label className="auth-label">IFSC Code</label>
          <input type="text" className="auth-input" placeholder="IFSC code" value={ifsc}
            onChange={(e) => setIfsc(e.target.value)} data-testid="bank-ifsc-input" />
        </div>
        <div>
          <label className="auth-label">Bank Name</label>
          <input type="text" className="auth-input" placeholder="Bank name" value={bankName}
            onChange={(e) => setBankName(e.target.value)} data-testid="bank-bankname-input" />
        </div>
        <div>
          <label className="auth-label">UPI ID (Optional)</label>
          <input type="text" className="auth-input" placeholder="yourname@upi" value={upiId}
            onChange={(e) => setUpiId(e.target.value)} data-testid="bank-upi-input" />
        </div>
        <button type="submit" className="btn-gold mt-2" disabled={loading} data-testid="bank-save-btn">
          {loading ? <Loader2 size={18} className="animate-spin mx-auto" /> : saved ? "Update Bank Details" : "Save Bank Details"}
        </button>
      </form>
    </div>
  );
}
